import { Badge } from "@/components/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/collapsible";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/tooltip";
import { useAudienceAndCampaignOptions } from "@/http/api-hooks";
import { ExperimentAnalysisExclusion, ExperimentAnalysisResultArtifact, ExperimentAnalysisRun } from "@/http/api-types";
import { cn } from "@/lib/utils";
import { AlertCircle, ChevronDown, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  ANALYSIS_STAGE_METRIC,
  AnalysisConversionSide,
  AnalysisConversionTotals,
  AnalysisForestRow,
  AnalysisMetric,
  AnalysisOutcomeCell,
  AnalysisStage,
  buildConversionTotals,
  buildForestRows,
  buildForestRowsForColumn,
  buildOutcomeMatrix,
  buildTestGroupColumns,
  CampaignOutcome,
  getAnalysisMemberLabel,
  getAnalysisStages,
  getAvailableTestIDs,
  getExclusions,
  getForestDomain,
  getSummaryStatus,
  hasConclusiveComparison,
  isExperimentAnalysisResultArtifact,
  percentChange,
} from "./analysis-result";

type AnalysisResultSectionProps = {
  run?: ExperimentAnalysisRun;
};

export function AnalysisResultSection({ run }: AnalysisResultSectionProps) {
  if (!run) {
    return (
      <AnalysisShell description="Schedule a run to inspect campaign-level lift, confidence, and distributions." />
    );
  }

  if (!run.terminal) {
    return (
      <AnalysisShell
        description="Analysis charts will appear when the selected run completes."
        body="The run is still in progress. Refresh the run status to load the analysis payload when it is ready."
      />
    );
  }

  const artifact = isExperimentAnalysisResultArtifact(run.result_artifact) ? run.result_artifact : null;

  if (!artifact) {
    // A payload exists but doesn't match the current shape → it's an older analysis format. We don't render
    // legacy shapes (no schema versioning); re-running regenerates it in the current format.
    const hasLegacyPayload = Boolean(run.result_artifact) && typeof run.result_artifact === "object";
    return (
      <AnalysisShell
        description={
          hasLegacyPayload
            ? "This analysis ran on an older result format and can't be displayed."
            : "Run completed, but the structured analysis payload is not available yet."
        }
        body={
          hasLegacyPayload
            ? "Re-run the analysis to regenerate it in the current format."
            : run.state_message || "The analysis result is still waiting on downstream data or artifact generation."
        }
        bodyTone="warning"
      />
    );
  }

  return <AnalysisContent artifact={artifact} />;
}

type AnalysisContentProps = {
  artifact: ExperimentAnalysisResultArtifact;
};

function AnalysisContent({ artifact }: AnalysisContentProps) {
  const isCustom = artifact.type === "custom";
  const memberLabel = getAnalysisMemberLabel();
  const stages = useMemo(() => getAnalysisStages(artifact), [artifact]);
  const [stage, setStage] = useState<AnalysisStage>("primary");
  // One metric per stage: the stage is the IPC/APC/ROAS lens the user picks.
  const metric: AnalysisMetric = ANALYSIS_STAGE_METRIC[stage];
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: audienceCampaignData } = useAudienceAndCampaignOptions({ campaign_type: "all" });
  const campaignInfoMap = useMemo(() => {
    const m: Record<string, { campaignName: string; audienceName: string }> = {};
    audienceCampaignData?.results?.forEach(r => {
      m[String(r.campaign_id)] = { campaignName: r.campaign_name, audienceName: r.audience_name };
    });
    return m;
  }, [audienceCampaignData]);

  // Tabs: test flights (global) or positional test-group columns (custom).
  const testIds = useMemo(() => getAvailableTestIDs(artifact), [artifact]);
  const groupColumns = useMemo(() => (isCustom ? buildTestGroupColumns(artifact) : []), [artifact, isCustom]);

  const [testId, setTestId] = useState(testIds[0] ?? "");
  const [column, setColumn] = useState(groupColumns[0] ?? 0);

  useEffect(() => {
    if (testIds.length && !testIds.includes(testId)) {
      setTestId(testIds[0]);
    }
  }, [testIds, testId]);

  useEffect(() => {
    if (groupColumns.length && !groupColumns.includes(column)) {
      setColumn(groupColumns[0]);
    }
  }, [groupColumns, column]);

  useEffect(() => {
    setExpandedId(null);
  }, [testId, column, stage]);

  const summaryKey = isCustom ? String(column) : testId;
  const buildRows = useCallback(
    (rowStage: AnalysisStage, rowMetric: AnalysisMetric) =>
      isCustom
        ? buildForestRowsForColumn(artifact, column, rowStage, rowMetric)
        : testId
          ? buildForestRows(artifact, testId, rowStage, rowMetric)
          : [],
    [artifact, isCustom, column, testId],
  );

  const matrix = useMemo(() => buildOutcomeMatrix(artifact, summaryKey, buildRows), [artifact, summaryKey, buildRows]);
  // Stay on a lens with data: the picked one can have none in another test group, or in an older result.
  useEffect(() => {
    const withData = matrix.filter(hasConclusiveComparison);
    if (withData.length && !withData.some(cell => cell.stage === stage)) {
      setStage(withData[0].stage);
    }
  }, [matrix, stage]);

  const rows = useMemo(() => buildRows(stage, metric), [buildRows, stage, metric]);
  const conversionTotals = useMemo(() => buildConversionTotals(rows), [rows]);
  const domain = useMemo(() => getForestDomain(rows), [rows]);
  const status = getSummaryStatus(artifact);
  const exclusions = getExclusions(artifact);

  return (
    <section>
      <header className="mb-6 flex flex-col gap-3 border-b border-border pb-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold tracking-tight">Experiment Analysis</h3>
            <p className="text-sm text-muted-foreground">
              Window {artifact.start} → {artifact.end} · Run {formatRunDate(artifact.run_date)} ·{" "}
              {Object.keys(artifact.variants).length > 1 ? "Controls" : "Control"}{" "}
              {Object.keys(artifact.variants).join(", ")}
            </p>
          </div>
          {isCustom
            ? groupColumns.length > 1 && (
                <TestVariantTabs
                  testIds={groupColumns.map(String)}
                  value={String(column)}
                  onChange={value => setColumn(Number(value))}
                  renderLabel={value => `Test Group ${Number(value) + 1}`}
                />
              )
            : testIds.length > 1 && <TestVariantTabs testIds={testIds} value={testId} onChange={setTestId} />}
        </div>

        {status ? <StatusBanner status={status} /> : null}
      </header>

      <div className="space-y-6">
        <OverallSummaryTable matrix={matrix} memberLabel={memberLabel} />

        <ConversionsTable totals={conversionTotals} hasRevenue={stages.includes("revenue")} />

        <OutcomeCards matrix={matrix} stage={stage} onSelect={setStage} />

        <ForestPlot
          rows={rows}
          domain={domain}
          stage={stage}
          memberLabel={memberLabel}
          expandedId={expandedId}
          onToggle={id => setExpandedId(prev => (prev === id ? null : id))}
          campaignInfoMap={campaignInfoMap}
        />

        {exclusions.length ? <ExclusionsFooter exclusions={exclusions} /> : null}
      </div>
    </section>
  );
}

// Each stage is presented to users as its per-EUR metric lens.
const STAGE_LABELS: Record<AnalysisStage, string> = { primary: "IPC", secondary: "APC", revenue: "ROAS" };

function stageLabel(stage: AnalysisStage): string {
  return STAGE_LABELS[stage];
}

// Who each stage's converters are, for explaining why a lens has no data.
const STAGE_CONVERTERS: Record<AnalysisStage, string> = {
  primary: "converting users",
  secondary: "users with actions",
  revenue: "paying users",
};

// Why a lens without a conclusive comparison shows no data (typically ROAS for audiences that hardly pay).
function noDataReason(cell: AnalysisOutcomeCell): string {
  if (!cell.analysed) {
    return "Not in this result: it was produced before this analysis was added.";
  }
  if (cell.total === 0) {
    return "No comparisons in this test group.";
  }
  const comparisons = cell.total === 1 ? "The only comparison was" : `All ${cell.total} comparisons were`;
  return `${comparisons} inconclusive: too few ${STAGE_CONVERTERS[cell.stage]} in control and test together, or no data on one side.`;
}

function OverallSummaryTable({ matrix, memberLabel }: { matrix: AnalysisOutcomeCell[]; memberLabel: string }) {
  return (
    <div className="space-y-2">
      <div>
        <div className="text-sm font-medium">Overall Summary</div>
        <div className="text-xs text-muted-foreground">
          {capitalize(memberLabel)} outcome counts from the analyser across each analysis slice.
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead className="px-3 py-2">Metric</TableHead>
              <TableHead className="px-3 py-2 text-right">Won</TableHead>
              <TableHead className="px-3 py-2 text-right">Neutral</TableHead>
              <TableHead className="px-3 py-2 text-right">Lost</TableHead>
              <TableHead className="px-3 py-2 text-right">Inconclusive</TableHead>
              <TableHead className="px-3 py-2 text-right">Total</TableHead>
              <TableHead className="px-3 py-2 text-right">Win Share</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {matrix.map(cell =>
              cell.analysed ? (
                <SummaryRow key={`${cell.stage}-${cell.metric}`} cell={cell} />
              ) : (
                <TableRow key={`${cell.stage}-${cell.metric}`} className="cursor-default">
                  <TableCell className="px-3 py-2 font-medium">{stageLabel(cell.stage)}</TableCell>
                  <TableCell colSpan={6} className="px-3 py-2 text-right text-xs text-muted-foreground">
                    No data. {noDataReason(cell)}
                  </TableCell>
                </TableRow>
              ),
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SummaryRow({ cell }: { cell: AnalysisOutcomeCell }) {
  return (
    <TableRow className="cursor-default">
      <TableCell className="px-3 py-2 font-medium">{stageLabel(cell.stage)}</TableCell>
      <TableCell className="px-3 py-2 text-right tabular-nums text-green-700">{formatInteger(cell.won)}</TableCell>
      <TableCell className="px-3 py-2 text-right tabular-nums text-amber-700">{formatInteger(cell.neutral)}</TableCell>
      <TableCell className="px-3 py-2 text-right tabular-nums text-red-700">{formatInteger(cell.lost)}</TableCell>
      <TableCell className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {formatInteger(cell.inconclusive)}
      </TableCell>
      <TableCell className="px-3 py-2 text-right tabular-nums">{formatInteger(cell.total)}</TableCell>
      <TableCell className="px-3 py-2 text-right tabular-nums">
        {formatShare(cell.won, cell.total - cell.inconclusive)}
      </TableCell>
    </TableRow>
  );
}

// The raw counts behind the IPC/APC/ROAS rates, for the whole test group. Spend rides along because the
// counts are only comparable at equal spend — IPC, APC and ROAS are these counts normalised by it. Revenue, 2nd AP
// users and payers read "—" for a result from before the revenue analysis.
function ConversionsTable({ totals, hasRevenue }: { totals: AnalysisConversionTotals; hasRevenue: boolean }) {
  // Revenue, 2nd AP users and payers came with the revenue analysis: "—" for a result from before it, otherwise the
  // total (0 when no campaign in the group has data on both sides).
  const reported = (value: number | undefined) => (hasRevenue ? (value ?? 0) : undefined);
  const control = {
    users: reported(totals.control.secondaryConverters),
    revenue: reported(totals.control.apCustomerRevenueEUR),
    payers: reported(totals.control.revenueConverters),
  };
  const test = {
    users: reported(totals.test.secondaryConverters),
    revenue: reported(totals.test.apCustomerRevenueEUR),
    payers: reported(totals.test.revenueConverters),
  };

  return (
    <div className="space-y-2">
      <div>
        <div className="text-sm font-medium">Conversions</div>
        <div className="text-xs text-muted-foreground">
          AP and 2nd AP conversion counts and AP customer revenue summed across the campaigns in this test group with
          data for both control and test. 2nd AP Users and Payers are the unique users with a 2nd AP conversion and with
          revenue. Δ is test versus control. Spend is supply cost (what we pay for the impressions, before margin); IPC,
          APC and ROAS are per EUR of it.
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead className="px-3 py-2">Variant</TableHead>
              <TableHead className="px-3 py-2 text-right">Spend</TableHead>
              <TableHead className="px-3 py-2 text-right">AP Conversions</TableHead>
              <TableHead className="px-3 py-2 text-right">2nd AP Conversions</TableHead>
              <TableHead className="px-3 py-2 text-right">2nd AP Users</TableHead>
              <TableHead className="px-3 py-2 text-right">AP Revenue</TableHead>
              <TableHead className="px-3 py-2 text-right">Payers</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <ConversionsSideRow label="Control" side={totals.control} {...control} />
            <ConversionsSideRow label="Test" side={totals.test} {...test} />
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="px-3 py-2">Δ</TableCell>
              <DeltaCell
                control={totals.control.supplyCostEUR}
                test={totals.test.supplyCostEUR}
                formatter={formatSignedCurrency}
              />
              <DeltaCell
                control={totals.control.apConversions}
                test={totals.test.apConversions}
                formatter={formatSignedInteger}
                tone
              />
              <DeltaCell
                control={totals.control.secondaryAPConversions}
                test={totals.test.secondaryAPConversions}
                formatter={formatSignedInteger}
                tone
              />
              <DeltaCell control={control.users} test={test.users} formatter={formatSignedInteger} tone />
              <DeltaCell control={control.revenue} test={test.revenue} formatter={formatSignedCurrency} tone />
              <DeltaCell control={control.payers} test={test.payers} formatter={formatSignedInteger} tone />
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
}

function ConversionsSideRow({
  label,
  side,
  users,
  revenue,
  payers,
}: {
  label: string;
  side: AnalysisConversionSide;
  users: number | undefined;
  revenue: number | undefined;
  payers: number | undefined;
}) {
  return (
    <TableRow>
      <TableCell className="px-3 py-2 font-medium">{label}</TableCell>
      <TableCell className="px-3 py-2 text-right tabular-nums">{formatCurrency(side.supplyCostEUR)}</TableCell>
      <TableCell className="px-3 py-2 text-right tabular-nums">{formatInteger(side.apConversions)}</TableCell>
      <TableCell className="px-3 py-2 text-right tabular-nums">{formatInteger(side.secondaryAPConversions)}</TableCell>
      <TableCell className="px-3 py-2 text-right tabular-nums">{formatInteger(users)}</TableCell>
      <TableCell className="px-3 py-2 text-right tabular-nums">{formatCurrency(revenue)}</TableCell>
      <TableCell className="px-3 py-2 text-right tabular-nums">{formatInteger(payers)}</TableCell>
    </TableRow>
  );
}

// `tone` colours the delta green/red. Spend leaves it off: spending more is context, not an outcome. A value the
// result doesn't report reads "—".
function DeltaCell({
  control,
  test,
  formatter,
  tone,
}: {
  control: number | undefined;
  test: number | undefined;
  formatter: (value: number) => string;
  tone?: boolean;
}) {
  if (control == null || test == null) {
    return <TableCell className="px-3 py-2 text-right text-muted-foreground">—</TableCell>;
  }

  const diff = test - control;
  const pct = percentChange(control, test);

  return (
    <TableCell
      className={cn(
        "px-3 py-2 text-right tabular-nums",
        tone && diff > 0 ? "text-green-700" : "",
        tone && diff < 0 ? "text-red-700" : "",
      )}
    >
      {formatter(diff)}
      <span className={cn("ml-1.5 font-normal", tone ? "" : "text-muted-foreground")}>({formatSignedPct(pct)})</span>
    </TableCell>
  );
}

function AnalysisShell({ description, body, bodyTone }: { description: string; body?: string; bodyTone?: "warning" }) {
  return (
    <section>
      <header className="mb-4 border-b border-border pb-2">
        <h3 className="text-lg font-semibold tracking-tight">Experiment Analysis</h3>
      </header>
      <p className="mb-4 text-sm text-muted-foreground">{description}</p>
      {body ? (
        <div
          className={cn(
            "rounded-lg border px-4 py-4 text-sm",
            bodyTone === "warning"
              ? "border-amber-200 bg-amber-50/70 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
              : "border-dashed bg-muted/30 text-muted-foreground",
          )}
        >
          {body}
        </div>
      ) : null}
    </section>
  );
}

function TestVariantTabs({
  testIds,
  value,
  onChange,
  renderLabel,
}: {
  testIds: string[];
  value: string;
  onChange: (next: string) => void;
  renderLabel?: (id: string) => string;
}) {
  return (
    <div className="inline-flex flex-wrap items-center gap-1 rounded-md border bg-muted/40 p-1">
      {testIds.map(id => (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          className={cn(
            "rounded px-2.5 py-1 text-xs font-medium transition-colors",
            value === id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {renderLabel ? renderLabel(id) : `Test ${id}`}
        </button>
      ))}
    </div>
  );
}

function StatusBanner({ status }: { status: string }) {
  const isWaiting = /wait|delay|pending/i.test(status);
  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-md border px-3 py-2 text-sm",
        isWaiting
          ? "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
          : "border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-100",
      )}
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{status}</span>
    </div>
  );
}

type OutcomeCardsProps = {
  matrix: AnalysisOutcomeCell[];
  stage: AnalysisStage;
  onSelect: (stage: AnalysisStage) => void;
};

function OutcomeCards({ matrix, stage, onSelect }: OutcomeCardsProps) {
  return (
    <div className="space-y-2">
      <div>
        <div className="text-sm font-medium">Outcomes</div>
        <div className="text-xs text-muted-foreground">
          Won / lost / neutral / inconclusive comparisons per metric. Select one to view it below.
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {matrix.map(cell =>
          hasConclusiveComparison(cell) ? (
            <OutcomeCard
              key={`${cell.stage}-${cell.metric}`}
              cell={cell}
              active={cell.stage === stage}
              onClick={() => onSelect(cell.stage)}
            />
          ) : (
            <NoDataOutcomeCard key={`${cell.stage}-${cell.metric}`} cell={cell} />
          ),
        )}
      </div>
    </div>
  );
}

// Keeps its place in the grid, so every result shows the same lenses, but can't be selected: there's nothing
// conclusive to plot.
function NoDataOutcomeCard({ cell }: { cell: AnalysisOutcomeCell }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-dashed bg-muted/20 p-3" aria-disabled="true">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {stageLabel(cell.stage)}
      </span>
      <span className="text-sm font-medium text-muted-foreground">No data</span>
      <span className="text-xs text-muted-foreground">{noDataReason(cell)}</span>
    </div>
  );
}

function OutcomeCard({ cell, active, onClick }: { cell: AnalysisOutcomeCell; active: boolean; onClick: () => void }) {
  const total = cell.total || 1;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col gap-2 rounded-lg border bg-card p-3 text-left transition-all hover:bg-muted/30",
        active ? "ring-2 ring-offset-1 ring-foreground/30 border-foreground/30" : "",
      )}
      aria-pressed={active}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {stageLabel(cell.stage)}
        </span>
      </div>

      <div className="flex h-2 overflow-hidden rounded-full bg-muted">
        <div className="bg-green-600" style={{ width: `${(cell.won / total) * 100}%` }} />
        <div className="bg-amber-500" style={{ width: `${(cell.neutral / total) * 100}%` }} />
        <div className="bg-red-600" style={{ width: `${(cell.lost / total) * 100}%` }} />
        <div className="bg-slate-400" style={{ width: `${(cell.inconclusive / total) * 100}%` }} />
      </div>

      <div className="text-xs text-muted-foreground">
        <span className="font-semibold text-green-700">{cell.won}W</span>
        <span className="mx-1">·</span>
        <span className="font-semibold text-red-700">{cell.lost}L</span>
        <span className="mx-1">·</span>
        <span className="font-semibold text-amber-700">{cell.neutral}N</span>
        <span className="mx-1">·</span>
        <span className="font-semibold text-slate-500">{cell.inconclusive}I</span>
      </div>
    </button>
  );
}

type ForestPlotProps = {
  rows: AnalysisForestRow[];
  domain: [number, number];
  stage: AnalysisStage;
  memberLabel: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  campaignInfoMap?: Record<string, { campaignName: string; audienceName: string }>;
};

function ForestPlot({ rows, domain, stage, memberLabel, expandedId, onToggle, campaignInfoMap }: ForestPlotProps) {
  // Inconclusive comparisons (too few converters, or no data on one side) have no lift to plot — they're hidden
  // here and only surfaced in the outcome cards' inconclusive counts.
  const plottable = rows.filter(row => row.liftMeanPct != null);
  const skipped = rows.length - plottable.length;
  const [lo, hi] = domain;
  const range = hi - lo || 1;
  const zeroPct = ((0 - lo) / range) * 100;
  const memberLabelPlural = `${memberLabel}s`;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-sm font-medium">
            {capitalize(memberLabelPlural)} · {stageLabel(stage)}
          </div>
          <div className="text-xs text-muted-foreground">
            {plottable.length} {memberLabel}
            {plottable.length === 1 ? "" : "s"} ranked by mean lift. Bar shows 90% interval; tick is mean. The 0% guide
            marks parity with control. Click a row for its control and test numbers.
          </div>
        </div>
        <ForestLegend />
      </div>

      <div className="rounded-lg border bg-card">
        <ForestAxis lo={lo} hi={hi} zeroPct={zeroPct} memberLabel={memberLabel} />
        <div>
          {plottable.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">
              No {memberLabel}-level data is available for this slice.
            </div>
          ) : (
            plottable.map(row => (
              <ForestRowItem
                key={row.campaignId}
                row={row}
                lo={lo}
                range={range}
                zeroPct={zeroPct}
                expanded={expandedId === row.campaignId}
                onToggle={() => onToggle(row.campaignId)}
                campaignInfo={campaignInfoMap?.[row.campaignId]}
              />
            ))
          )}
        </div>
      </div>

      {skipped > 0 ? (
        <div className="text-xs text-muted-foreground">
          {skipped} {memberLabel}
          {skipped === 1 ? "" : "s"} hidden — inconclusive (too few converters, or no data on one side).
        </div>
      ) : null}
    </div>
  );
}

function ForestLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
      <LegendLine color="bg-sky-500/50" label="0% = parity" />
      <LegendSwatch color="bg-green-600" label="Win (p ≥ 95% or CI > 0)" />
      <LegendSwatch color="bg-red-600" label="CI < 0" />
      <LegendSwatch color="bg-slate-400" label="Crosses 0" />
    </div>
  );
}

function LegendLine({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("inline-block h-3 w-px", color)} />
      {label}
    </span>
  );
}

function InlineValueTooltip({ label, content, className }: { label: string; content: string; className?: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "cursor-help underline decoration-dotted underline-offset-2 hover:text-foreground/90",
            className,
          )}
          onClick={event => event.stopPropagation()}
        >
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-left">
        {content}
      </TooltipContent>
    </Tooltip>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("inline-block h-2 w-3 rounded-full", color)} />
      {label}
    </span>
  );
}

function ForestAxis({
  lo,
  hi,
  zeroPct,
  memberLabel,
}: {
  lo: number;
  hi: number;
  zeroPct: number;
  memberLabel: string;
}) {
  return (
    <div className="flex border-b bg-muted/30 text-xs text-muted-foreground">
      <div className="w-[200px] shrink-0 px-3 py-2 font-medium uppercase tracking-wide">{memberLabel}</div>
      <div className="flex flex-1 items-center gap-3 py-2 pr-3">
        <div className="relative h-4 flex-1">
          <span className="absolute left-0">{formatPct(lo)}</span>
          <span
            className="absolute top-0 -translate-x-1/2 font-medium text-foreground"
            style={{ left: `${clamp(zeroPct)}%` }}
          >
            0%
          </span>
          <span className="absolute right-0">{formatPct(hi)}</span>
          <div
            className="pointer-events-none absolute -bottom-2 -top-2 w-px bg-sky-500/50"
            style={{ left: `${clamp(zeroPct)}%` }}
          />
        </div>

        <div className="w-32 shrink-0 text-right font-medium uppercase tracking-wide">Estimate</div>
      </div>
    </div>
  );
}

type ForestRowItemProps = {
  row: AnalysisForestRow;
  lo: number;
  range: number;
  zeroPct: number;
  expanded: boolean;
  onToggle: () => void;
  campaignInfo?: { campaignName: string; audienceName: string };
};

function ForestRowItem({ row, lo, range, zeroPct, expanded, onToggle, campaignInfo }: ForestRowItemProps) {
  const meanPct = row.liftMeanPct != null ? ((row.liftMeanPct - lo) / range) * 100 : null;
  const ciLo = row.liftCILowerPct != null ? ((row.liftCILowerPct - lo) / range) * 100 : null;
  const ciHi = row.liftCIUpperPct != null ? ((row.liftCIUpperPct - lo) / range) * 100 : null;

  const outcomeColor = outcomeFill(row.outcome);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <div className="border-b last:border-b-0">
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        aria-expanded={expanded}
        className="flex cursor-pointer items-center transition-colors hover:bg-muted/40"
      >
        <div className="flex w-[200px] shrink-0 items-center gap-2 px-3 py-3">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0">
            <div className="whitespace-nowrap text-sm font-medium">
              <span className="font-mono">[{row.campaignId}]</span>
              {campaignInfo ? ` ${campaignInfo.campaignName}` : ""}
            </div>
            {campaignInfo && (
              <div className="truncate text-xs text-muted-foreground" title={campaignInfo.audienceName}>
                {campaignInfo.audienceName}
              </div>
            )}
            <div className="truncate text-xs text-muted-foreground">{formatCurrency(row.test.supply_cost_eur)}</div>
          </div>
        </div>

        <div className="flex flex-1 items-center gap-3 py-3 pr-3">
          <div className="relative h-6 flex-1">
            <div
              className="pointer-events-none absolute inset-y-0 w-px bg-sky-500/40"
              style={{ left: `${clamp(zeroPct)}%` }}
            />
            {ciLo != null && ciHi != null ? (
              <div
                className={cn("absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full", outcomeColor.bar)}
                style={{
                  left: `${clamp(ciLo)}%`,
                  width: `${Math.max(clamp(ciHi) - clamp(ciLo), 0.4)}%`,
                }}
              />
            ) : null}
            {meanPct != null ? (
              <div
                className={cn(
                  "absolute top-1/2 h-4 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full ring-1 ring-background",
                  outcomeColor.marker,
                )}
                style={{
                  left: `${clamp(meanPct)}%`,
                }}
              />
            ) : null}
          </div>

          <TooltipProvider delayDuration={0} skipDelayDuration={0} disableHoverableContent>
            <div className="w-32 shrink-0 text-right text-xs font-medium tabular-nums text-foreground">
              <InlineValueTooltip
                label={formatPct(row.liftMeanPct)}
                content="Mean lift versus control for this campaign and metric. This is the same value shown by the vertical tick."
              />
              {row.pTestBetter != null ? (
                <InlineValueTooltip
                  className="ml-2 text-muted-foreground"
                  label={`p=${(row.pTestBetter * 100).toFixed(0)}%`}
                  content="Estimated probability that the test beats control. This is not a classical p-value."
                />
              ) : null}
            </div>
          </TooltipProvider>
        </div>
      </div>

      {expanded ? <ForestRowDetail row={row} /> : null}
    </div>
  );
}

// The comparison behind a row, short and wide: its lift, then the control and test numbers side by side.
function ForestRowDetail({ row }: { row: AnalysisForestRow }) {
  return (
    <div className="space-y-3 border-t bg-muted/20 p-4">
      <div className="grid grid-cols-3 gap-2">
        <DetailStat label="Mean lift" value={formatPct(row.liftMeanPct)} />
        <DetailStat label="90% CI" value={formatPctRange(row.liftCILowerPct, row.liftCIUpperPct)} />
        <DetailStat label="Pr(test > control)" value={formatProbability(row.pTestBetter)} />
      </div>

      <div className="rounded-md border bg-background p-2.5">
        <div className="grid grid-cols-[auto_repeat(10,minmax(0,1fr))] gap-x-3 gap-y-1.5 text-xs">
          <span />
          <span className="text-right font-medium text-muted-foreground">Spend</span>
          <span className="text-right font-medium text-muted-foreground">Impressions</span>
          <span className="text-right font-medium text-muted-foreground">IPC</span>
          <span className="text-right font-medium text-muted-foreground">APC</span>
          <span className="text-right font-medium text-muted-foreground">ROAS</span>
          <span className="text-right font-medium text-muted-foreground">AP Conv</span>
          <span className="text-right font-medium text-muted-foreground">2nd AP Conv</span>
          <span className="text-right font-medium text-muted-foreground">2nd AP Users</span>
          <span className="text-right font-medium text-muted-foreground">AP Revenue</span>
          <span className="text-right font-medium text-muted-foreground">Payers</span>

          <span className="font-medium text-muted-foreground">Control</span>
          <span className="text-right tabular-nums">{formatCurrency(row.control.supply_cost_eur)}</span>
          <span className="text-right tabular-nums">{formatInteger(row.control.impressions)}</span>
          <span className="text-right tabular-nums">{formatRate(row.control.IPC)}</span>
          <span className="text-right tabular-nums">{formatRate(row.control.APC)}</span>
          <span className="text-right tabular-nums">{formatROAS(row.control.ROAS)}</span>
          <span className="text-right tabular-nums">{formatInteger(row.control.ap_conversions ?? 0)}</span>
          <span className="text-right tabular-nums">{formatInteger(row.control.secondary_ap_conversions ?? 0)}</span>
          <span className="text-right tabular-nums">{formatInteger(row.control.secondary_converters)}</span>
          <span className="text-right tabular-nums">{formatCurrency(row.control.ap_customer_revenue_eur)}</span>
          <span className="text-right tabular-nums">{formatInteger(row.control.revenue_converters)}</span>

          <span className="font-medium text-muted-foreground">Test</span>
          <span className="text-right tabular-nums">{formatCurrency(row.test.supply_cost_eur)}</span>
          <span className="text-right tabular-nums">{formatInteger(row.test.impressions)}</span>
          <span className="text-right tabular-nums">{formatRate(row.test.IPC)}</span>
          <span className="text-right tabular-nums">{formatRate(row.test.APC)}</span>
          <span className="text-right tabular-nums">{formatROAS(row.test.ROAS)}</span>
          <span className="text-right tabular-nums">{formatInteger(row.test.ap_conversions ?? 0)}</span>
          <span className="text-right tabular-nums">{formatInteger(row.test.secondary_ap_conversions ?? 0)}</span>
          <span className="text-right tabular-nums">{formatInteger(row.test.secondary_converters)}</span>
          <span className="text-right tabular-nums">{formatCurrency(row.test.ap_customer_revenue_eur)}</span>
          <span className="text-right tabular-nums">{formatInteger(row.test.revenue_converters)}</span>
        </div>
      </div>
    </div>
  );
}

function DetailStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-background px-2.5 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function ExclusionsFooter({ exclusions }: { exclusions: ExperimentAnalysisExclusion[] }) {
  return (
    <Collapsible>
      <CollapsibleTrigger className="inline-flex items-center gap-1.5 rounded-md border bg-muted/40 px-2.5 py-1 text-sm font-medium text-foreground transition-colors hover:bg-muted [&[data-state=open]>svg]:rotate-90">
        <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform duration-200" />
        <span>Excluded campaigns</span>
        <span className="text-xs text-muted-foreground">({exclusions.length})</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 overflow-hidden rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Campaign</th>
              <th className="px-3 py-2 text-left font-medium">Flight</th>
              <th className="px-3 py-2 text-right font-medium">Spend</th>
              <th className="px-3 py-2 text-left font-medium">Reason</th>
            </tr>
          </thead>
          <tbody>
            {exclusions.map((exclusion, index) => (
              <tr key={`${exclusion.campaign}-${exclusion.primary_prediction_flight_id}-${index}`} className="border-t">
                <td className="px-3 py-2 font-mono">{exclusion.campaign}</td>
                <td className="px-3 py-2 font-mono text-muted-foreground">{exclusion.primary_prediction_flight_id}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(exclusion.supply_cost_eur)}</td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-700">
                    {exclusion.reason}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CollapsibleContent>
    </Collapsible>
  );
}

function clamp(value: number) {
  return Math.max(0, Math.min(100, value));
}

function outcomeFill(outcome: CampaignOutcome) {
  switch (outcome) {
    case "positive":
      return { bar: "bg-green-500/70", marker: "bg-green-600" };
    case "negative":
      return { bar: "bg-red-500/70", marker: "bg-red-600" };
    default:
      return { bar: "bg-slate-400/60", marker: "bg-slate-500" };
  }
}

function formatRunDate(value: string) {
  if (!/^\d{8}$/.test(value)) {
    return value;
  }
  return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
}

function formatPct(value: number | null) {
  if (value == null) {
    return "—";
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)}%`;
}

function formatPctRange(lower: number | null, upper: number | null) {
  if (lower == null || upper == null) {
    return "—";
  }
  return `${formatPct(lower)} → ${formatPct(upper)}`;
}

function formatProbability(value: number | null) {
  if (value == null) {
    return "—";
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value * 100)}%`;
}

function formatShare(value: number, total: number) {
  if (total <= 0) {
    return "—";
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format((value / total) * 100)}%`;
}

// The stat formatters render "—" for a value the artifact doesn't carry (a variant without data in a comparison,
// or revenue in a result from before the revenue analysis).
function formatInteger(value: number | undefined) {
  if (value == null) {
    return "—";
  }
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

function formatRate(value: number | undefined) {
  if (value == null) {
    return "—";
  }
  return new Intl.NumberFormat(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(value);
}

// ROAS is customer revenue per EUR of supply cost, shown as a percentage.
function formatROAS(value: number | undefined) {
  if (value == null) {
    return "—";
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value * 100)}%`;
}

function formatSignedInteger(value: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0, signDisplay: "exceptZero" }).format(value);
}

function formatSignedPct(value: number | null) {
  if (value == null) {
    return "—";
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, signDisplay: "exceptZero" }).format(value)}%`;
}

function formatSignedCurrency(value: number) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
    signDisplay: "exceptZero",
  }).format(value);
}

function formatCurrency(value: number | undefined) {
  if (value == null) {
    return "—";
  }
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
